/**
 * CLIENT PORTAL — surface content (route `/portal`, deck open question
 * "client portal as module 4").
 *
 * Provenance, per figure:
 *  - CORE (imported, never re-stated): go-live date/label/status, requirement
 *    totals, build counters, the 7-phase lifecycle, the client contact.
 *  - DECK (transcribed here): decision D-04 "Overlapping promotions" with its
 *    `Waiting · 6 days`, `+21 h if priority field` and `Blocks epic 3` pills
 *    (mockups 3.4); the blueprint's 6 epics (3.4); the 9 key users who test at
 *    UAT (3.7); "four in five delay days sit on the client side" (closing).
 *  - INVENTED ONCE, HERE (the deck never draws this screen): the two signed
 *    gate dates, the second open decision D-06, change request CR-07, and the
 *    UAT-window ask. Every one of them is dated relative to NOW or to the
 *    project's go-live — nothing is computed from the wall clock.
 *
 * Client rule (deck 0.3): a client sees gates, decisions, UAT and progress —
 * never raw agent output, another client, or cost rates. Nothing in this
 * module may carry a rate, a margin, a budget or an agent run.
 */

import {
  CLIENT_CONTACT,
  NOW,
  PHASES,
  VAN_DIJK,
  heroProject,
  phaseIndex,
  type PhaseKey,
} from '@/demo/data'

/* ── Date arithmetic (relative to NOW / go-live — never Date.now()) ─────── */

const MS_DAY = 86_400_000

/** Project dates are day-precision; anchor them at the NOW time-of-day. */
const atProjectHour = (isoDay: string): Date => new Date(`${isoDay}T09:00:00+02:00`)

const shiftDays = (from: Date, days: number): Date => new Date(from.getTime() + days * MS_DAY)

const daysBetween = (from: Date, to: Date): number =>
  Math.round((to.getTime() - from.getTime()) / MS_DAY)

const ZONE = 'Europe/Amsterdam'
const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: ZONE })
const longDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: ZONE })
const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: ZONE })

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`

const phase = (key: PhaseKey) => {
  const found = PHASES.find((p) => p.key === key)
  if (!found) throw new Error(`Unknown phase: ${key}`)
  return found
}

/* ── The rollout the client is looking at ───────────────────────────────── */

const HERO = heroProject()

/**
 * The page header, spoken to the client: their own rollout, its health, the
 * date they planned their year around, and where it sits in the seven phases.
 */
export const PORTAL_HEADER = {
  title: `Your Odoo ${HERO.odooVersion} rollout`,
  client: HERO.client,
  health: HERO.health,
  goLiveLabel: VAN_DIJK.goLive.label,
  goLiveWeeksAway: VAN_DIJK.goLive.weeksAway,
  currentPhase: HERO.phase,
  phaseName: phase(HERO.phase).name,
  phaseNumber: phaseIndex(HERO.phase) + 1,
  phaseCount: PHASES.length,
} as const

const GO_LIVE_DATE = atProjectHour(VAN_DIJK.goLive.date)

/**
 * UAT opens six weeks before go-live — the Test phase gate is the client's
 * next signature, so the portal states the date the client has to plan around.
 * Derived, never typed: shifting go-live moves this with it.
 */
const UAT_LEAD_DAYS = 42
const UAT_START_DATE = shiftDays(GO_LIVE_DATE, -UAT_LEAD_DAYS)

export const UAT_START = {
  label: shortDate.format(UAT_START_DATE),
  weeksAway: Math.round(daysBetween(NOW, UAT_START_DATE) / 7),
  weeksBeforeGoLive: UAT_LEAD_DAYS / 7,
} as const

/** Deck 3.7: nine key users test this rollout. */
export const UAT_KEY_USERS = 9

/* ── What the client owes us (the closing-slide argument, made actionable) ─ */

export type OwedTone = 'blocked' | 'review' | 'neutral'

export interface ClientOwedItem {
  id: string
  /** `decision` items are the ones the "your open decisions" KPI counts. */
  kind: 'decision' | 'schedule' | 'change-request'
  title: string
  detail: string
  tone: OwedTone
  /** Lateness/waiting state, e.g. "2 days late" — carried on a pill. */
  stateLabel: string
  /** When it reached the client, so the state is auditable. */
  askedLabel: string
  /** Deck-verbatim consequence chips (effort and blocked work — never cost). */
  pills: readonly string[]
}

const UAT_DATES_ASKED = shiftDays(NOW, -8)
const UAT_DATES_DUE = shiftDays(NOW, -2)
const D04_RAISED = shiftDays(NOW, -6)
const D06_RAISED = shiftDays(NOW, -2)
const CR07_RAISED = shiftDays(NOW, -1)
const CR07_DUE = shiftDays(NOW, 1)

const uatDaysLate = daysBetween(UAT_DATES_DUE, NOW)
const d04WaitingDays = daysBetween(D04_RAISED, NOW)
const d06WaitingDays = daysBetween(D06_RAISED, NOW)

/**
 * Ordered by severity — late first, then longest wait. The same ids, the same
 * lateness and the same consequence chips the internal board shows; the portal
 * re-frames them in the client's language, it does not soften them.
 */
export const CLIENT_OWED: readonly ClientOwedItem[] = [
  {
    id: 'uat-window',
    kind: 'schedule',
    title: 'Confirm UAT dates',
    detail: `We need your key users' availability to lock the UAT window.`,
    tone: 'blocked',
    stateLabel: `${plural(uatDaysLate, 'day')} late`,
    askedLabel: `asked ${shortDate.format(UAT_DATES_ASKED)} · due ${shortDate.format(UAT_DATES_DUE)}`,
    pills: [`Holds the ${UAT_START.label} UAT start`],
  },
  {
    id: 'D-04',
    kind: 'decision',
    title: 'Answer decision D-04 · Overlapping promotions',
    detail: 'Which rule wins when two promotions apply to the same basket line.',
    tone: 'review',
    stateLabel: `Waiting · ${plural(d04WaitingDays, 'day')}`,
    askedLabel: `raised ${shortDate.format(D04_RAISED)}`,
    pills: ['+21 h if priority field', 'Blocks epic 3'],
  },
  {
    id: 'D-06',
    kind: 'decision',
    title: 'Answer decision D-06 · Returns without a receipt',
    detail: 'Refund to card or store credit — your policy decides which flow we build.',
    tone: 'review',
    stateLabel: `Waiting · ${plural(d06WaitingDays, 'day')}`,
    askedLabel: `raised ${shortDate.format(D06_RAISED)}`,
    pills: [],
  },
  {
    id: 'CR-07',
    kind: 'change-request',
    title: 'Sign off blueprint change CR-07',
    detail: 'A change to the blueprint you signed needs your signature before we build it.',
    tone: 'neutral',
    stateLabel: `Due ${weekday.format(CR07_DUE)} ${shortDate.format(CR07_DUE)}`,
    askedLabel: `sent ${shortDate.format(CR07_RAISED)}`,
    pills: [],
  },
] as const

export const OPEN_DECISIONS: readonly ClientOwedItem[] = CLIENT_OWED.filter(
  (item) => item.kind === 'decision'
)

/** "D-04 · D-06" — the KPI qualifier names what it counts. */
export const OPEN_DECISION_REFS = OPEN_DECISIONS.map((d) => d.id).join(' · ')

/* ── Gates the client has already signed ────────────────────────────────── */

export interface SignedGate {
  phaseKey: PhaseKey
  phaseName: string
  subLabel: string
  /** The artefact the signature is attached to. */
  artefact: string
  signedBy: string
  signedOn: string
  /** What signing that gate released — one line, no internal detail. */
  released: string
}

/**
 * Invented once, here: a rollout that started in spring 2026 and reaches
 * go-live on 12 Nov signs Discover in late May and Design in early July.
 */
const DISCOVER_SIGNED = atProjectHour('2026-05-22')
const DESIGN_SIGNED = atProjectHour('2026-07-03')

const BLUEPRINT_EPICS = 6 // deck 3.4: "Solution blueprint · 6 epics"

export const SIGNED_GATES: readonly SignedGate[] = [
  {
    phaseKey: 'discover',
    phaseName: phase('discover').name,
    subLabel: phase('discover').subLabel,
    artefact: 'Scope & fit-gap report',
    signedBy: CLIENT_CONTACT.name,
    signedOn: longDate.format(DISCOVER_SIGNED),
    released: `${VAN_DIJK.requirements.total} requirements baselined · Design opened`,
  },
  {
    phaseKey: 'design',
    phaseName: phase('design').name,
    subLabel: phase('design').subLabel,
    artefact: `Solution blueprint · ${BLUEPRINT_EPICS} epics`,
    signedBy: CLIENT_CONTACT.name,
    signedOn: longDate.format(DESIGN_SIGNED),
    released: 'Build started · change after this point goes through a CR',
  },
] as const

/* ── Progress, in the client's terms (no cost, no rates, no agent output) ── */

export interface ProgressLine {
  label: string
  figure: string
  detail: string
}

export const PORTAL_PROGRESS: readonly ProgressLine[] = [
  {
    label: 'Modules delivered',
    figure: `${VAN_DIJK.build.modulesDone} of ${VAN_DIJK.build.modulesTotal}`,
    detail: 'Configured and internally reviewed. You see them yourself at UAT.',
  },
  {
    label: 'UAT preparation',
    figure: `${UAT_KEY_USERS} key users`,
    detail: `Test cases are written from the requirements you approved. UAT opens ${UAT_START.label}, ${UAT_START.weeksBeforeGoLive} weeks before go-live — the dates are still open above.`,
  },
  {
    label: 'Next milestone',
    figure: `${phase('test').name} gate · ${UAT_START.label}`,
    detail: `${phase('test').gate} — your next signature. The ${phase('build').name} and ${phase('migrate').name} gates before it are ours to pass, not yours.`,
  },
] as const
